import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { AlertCircle, Check, MailCheck, MailWarning } from "lucide-react";
import type { ApiError } from "../api/http";
import { useInviteResolve, useJoinBusiness } from "../hooks/useJoinBusiness";
import { useAcceptInvite } from "../hooks/useAcceptInvite";
import { useResendVerification } from "../hooks/useResendVerification";
import { useResendCooldown } from "../hooks/useResendCooldown";
import { setPendingVerificationEmail } from "../utils/pendingVerification";
import { AuthLayout } from "../components/auth/AuthLayout";
import { PasswordInput } from "../components/auth/PasswordInput";
import {
  authFieldError,
  authFormError,
  authInput,
  authLabel,
  authLink,
  authMuted,
  authPrimaryBtn,
  authSecondaryBtn,
} from "../components/auth/authStyles";

/**
 * Single-page employee join (replaces the old auto-accept). The invite link
 * resolves to the business + invited role, then a brand-new invitee sets their
 * name + password in one step. An already-registered invitee is routed to the
 * lightweight "accept invitation" action instead of re-registering.
 */
export function JoinBusinessPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const resolve = useInviteResolve(token);
  const join = useJoinBusiness();
  const accept = useAcceptInvite();
  const resendVerification = useResendVerification();
  const { cooldown, start: startCooldown } = useResendCooldown("join");

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    password: "",
    password_confirm: "",
  });
  const [tosAccepted, setTosAccepted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // ── Token / resolution states ─────────────────────────────────────────────
  if (!token) {
    return (
      <AuthLayout>
        <InfoState
          title={t("auth.joinBusiness.invalidTitle")}
          body={t("auth.joinBusiness.invalidBody")}
        />
      </AuthLayout>
    );
  }

  if (resolve.isPending) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500 dark:border-white/10 dark:border-t-blue-400" />
          <p className={`mt-4 text-sm ${authMuted}`}>
            {t("auth.joinBusiness.loadingInvite")}
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (resolve.isError || !resolve.data) {
    return (
      <AuthLayout>
        <InfoState
          title={t("auth.joinBusiness.invalidTitle")}
          body={t("auth.joinBusiness.invalidBody")}
        />
      </AuthLayout>
    );
  }

  const invite = resolve.data;

  // ── Join succeeded — account created, verification email sent ─────────────
  if (join.isSuccess) {
    return (
      <AuthLayout>
        <div className="text-center">
          <SuccessIcon icon={<MailCheck size={22} />} />
          <h2 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">
            {t("auth.joinBusiness.successTitle")}
          </h2>
          <p className={`mt-2 text-sm ${authMuted}`}>
            {t("auth.joinBusiness.successBody", { email: invite.email })}
          </p>
          <Link to="/login" className={`mt-6 inline-block text-sm ${authLink}`}>
            {t("auth.joinBusiness.gotoLogin")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // ── Existing account accepted the invite ──────────────────────────────────
  if (accept.isSuccess) {
    return (
      <AuthLayout>
        <div className="text-center">
          <SuccessIcon icon={<Check size={22} />} />
          <h2 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">
            {t("auth.joinBusiness.acceptedTitle")}
          </h2>
          <p className={`mt-2 text-sm ${authMuted}`}>{accept.data.detail}</p>
          <Link
            to="/login"
            className={`mt-6 inline-block ${authPrimaryBtn} max-w-[12rem]`}
          >
            {t("auth.joinBusiness.gotoLogin")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const invitedByLine = invite.inviter_name
    ? t("auth.joinBusiness.invitedBy", {
        inviter: invite.inviter_name,
        role: invite.role_name,
      })
    : t("auth.joinBusiness.invitedByNoName", { role: invite.role_name });

  // ── Invited-but-unverified — verify first, then log in ────────────────────
  // Such an account can neither re-register (join rejects the duplicate email)
  // nor accept (accept requires an active user) — without this branch they'd
  // see an Accept button that always errors.
  if (invite.account_exists && !invite.account_active) {
    const handleResendVerification = async () => {
      if (cooldown > 0 || resendVerification.isPending) return;
      try {
        await resendVerification.mutateAsync({ email: invite.email });
      } catch {
        // Enumeration-safe endpoint — same affordance either way.
      }
      setPendingVerificationEmail(invite.email);
      startCooldown();
    };
    return (
      <AuthLayout title={t("auth.joinBusiness.title", { business: invite.business_name })}>
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 ring-1 ring-amber-200 text-amber-600 dark:bg-amber-500/15 dark:ring-amber-500/30 dark:text-amber-300">
            <MailWarning size={22} />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">
            {t("auth.joinBusiness.verifyFirstTitle")}
          </h2>
          <p className={`mt-2 text-sm ${authMuted}`}>
            {t("auth.joinBusiness.verifyFirstBody", {
              email: invite.email,
              business: invite.business_name,
            })}
          </p>
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={cooldown > 0 || resendVerification.isPending}
            className={`mt-5 ${authSecondaryBtn}`}
          >
            {resendVerification.isPending
              ? t("auth.verify.resending")
              : cooldown > 0
                ? t("auth.verify.resendCooldown", { seconds: cooldown })
                : t("auth.joinBusiness.resendVerification")}
          </button>
          <Link to="/login" className={`mt-4 block text-sm ${authLink}`}>
            {t("auth.joinBusiness.gotoLogin")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // ── Already-registered invitee — accept, don't re-register ────────────────
  if (invite.account_exists) {
    const acceptError = accept.error as (ApiError & { errors?: { code?: string } }) | null;
    const acceptErrorText = (() => {
      if (!accept.isError || !acceptError) return null;
      const code = (acceptError.errors as { code?: string } | undefined)?.code;
      if (code === "already_member") return t("auth.joinBusiness.alreadyMember");
      if (code === "invitation_expired") return t("auth.joinBusiness.expired");
      return acceptError.detail ?? t("auth.joinBusiness.genericError");
    })();
    return (
      <AuthLayout title={t("auth.joinBusiness.title", { business: invite.business_name })}>
        <p className={`text-sm ${authMuted}`}>{invitedByLine}</p>
        <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">
          {t("auth.joinBusiness.existingBody", {
            email: invite.email,
            business: invite.business_name,
          })}
        </p>
        {acceptErrorText && (
          <div className={`${authFormError} mt-4`}>{acceptErrorText}</div>
        )}
        <button
          type="button"
          onClick={() => accept.mutate({ token })}
          disabled={accept.isPending}
          className={`mt-5 ${authPrimaryBtn}`}
        >
          {accept.isPending
            ? t("auth.joinBusiness.accepting")
            : t("auth.joinBusiness.acceptCta")}
        </button>
      </AuthLayout>
    );
  }

  // ── Create-account-and-join form ──────────────────────────────────────────
  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.first_name)
      next.first_name = t("auth.register.validation.firstNameRequired");
    if (!form.last_name)
      next.last_name = t("auth.register.validation.lastNameRequired");
    if (form.password.length < 8)
      next.password = t("auth.register.validation.passwordTooShort");
    if (form.password !== form.password_confirm)
      next.password_confirm = t("auth.register.validation.passwordsDoNotMatch");
    if (!tosAccepted) next.tos_accepted = t("legal.acceptance.checkboxRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setErrors({});
    const language = (i18n.language ?? "en").split("-")[0] === "es" ? "es" : "en";
    try {
      await join.mutateAsync({
        token,
        first_name: form.first_name,
        last_name: form.last_name,
        password: form.password,
        password_confirm: form.password_confirm,
        tos_accepted: tosAccepted,
        language,
      });
      // Pre-fills the resend form on VerifyEmailPage if the link never arrives.
      setPendingVerificationEmail(invite.email);
    } catch (err) {
      const apiError = err as ApiError;
      const fieldErrors: Record<string, string> = {};
      if (apiError.errors && typeof apiError.errors === "object") {
        for (const [key, value] of Object.entries(apiError.errors)) {
          if (Array.isArray(value)) fieldErrors[key] = value[0];
        }
      }
      setErrors(
        Object.keys(fieldErrors).length > 0
          ? fieldErrors
          : { form: apiError.detail || t("auth.joinBusiness.genericError") }
      );
    }
  };

  return (
    <AuthLayout
      title={t("auth.joinBusiness.title", { business: invite.business_name })}
      width="md"
    >
      <p className={`-mt-2 mb-4 text-sm ${authMuted}`}>{invitedByLine}</p>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {errors.form && <div className={authFormError}>{errors.form}</div>}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="first_name" className={authLabel}>
              {t("auth.register.firstName")}
            </label>
            <input
              id="first_name"
              type="text"
              required
              autoComplete="given-name"
              value={form.first_name}
              onChange={(e) => updateField("first_name", e.target.value)}
              className={authInput}
            />
            {errors.first_name && <p className={authFieldError}>{errors.first_name}</p>}
          </div>
          <div>
            <label htmlFor="last_name" className={authLabel}>
              {t("auth.register.lastName")}
            </label>
            <input
              id="last_name"
              type="text"
              required
              autoComplete="family-name"
              value={form.last_name}
              onChange={(e) => updateField("last_name", e.target.value)}
              className={authInput}
            />
            {errors.last_name && <p className={authFieldError}>{errors.last_name}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="email" className={authLabel}>
            {t("auth.register.email")}
          </label>
          {/* The invited address is fixed — the invitation is bound to it. */}
          <input
            id="email"
            type="email"
            value={invite.email}
            readOnly
            disabled
            className={`${authInput} cursor-not-allowed opacity-70`}
          />
          <p className={`mt-1 text-xs ${authMuted}`}>
            {t("auth.joinBusiness.emailLocked")}
          </p>
        </div>

        <div>
          <label htmlFor="password" className={authLabel}>
            {t("auth.register.password")}
          </label>
          <PasswordInput
            id="password"
            required
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => updateField("password", e.target.value)}
            placeholder={t("auth.register.passwordPlaceholder")}
          />
          {errors.password && <p className={authFieldError}>{errors.password}</p>}
        </div>

        <div>
          <label htmlFor="password_confirm" className={authLabel}>
            {t("auth.register.confirmPassword")}
          </label>
          <PasswordInput
            id="password_confirm"
            required
            autoComplete="new-password"
            value={form.password_confirm}
            onChange={(e) => updateField("password_confirm", e.target.value)}
            placeholder={t("auth.register.confirmPlaceholder")}
          />
          {errors.password_confirm && (
            <p className={authFieldError}>{errors.password_confirm}</p>
          )}
        </div>

        <div className="pt-1">
          <label className="flex cursor-pointer select-none items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input
              id="tos_accepted"
              type="checkbox"
              checked={tosAccepted}
              onChange={(e) => {
                setTosAccepted(e.target.checked);
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.tos_accepted;
                  return next;
                });
              }}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500 dark:border-white/20 dark:bg-white/5 dark:text-blue-500"
            />
            <span className="leading-snug">
              <Trans
                i18nKey="legal.acceptance.checkboxLabel"
                components={{
                  terms: (
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" className={authLink} />
                  ),
                  privacy: (
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" className={authLink} />
                  ),
                }}
              />
            </span>
          </label>
          {errors.tos_accepted && <p className={authFieldError}>{errors.tos_accepted}</p>}
        </div>

        <button
          type="submit"
          disabled={join.isPending || !tosAccepted}
          className={authPrimaryBtn}
        >
          {join.isPending
            ? t("auth.joinBusiness.submitting")
            : t("auth.joinBusiness.submit")}
        </button>
      </form>

      <div className={`mt-5 border-t border-slate-200 pt-4 text-center text-sm dark:border-white/5 ${authMuted}`}>
        {t("auth.register.loginPrompt")}{" "}
        <Link to="/login" className={authLink}>
          {t("auth.register.loginLink")}
        </Link>
      </div>
    </AuthLayout>
  );
}

function InfoState({ title, body }: { title: string; body: string }) {
  const { t } = useTranslation();
  return (
    <div className="text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 ring-1 ring-rose-200 text-rose-600 dark:bg-rose-500/15 dark:ring-rose-500/30 dark:text-rose-300">
        <AlertCircle size={22} />
      </div>
      <h2 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">{title}</h2>
      <p className={`mt-2 text-sm ${authMuted}`}>{body}</p>
      <Link to="/login" className={`mt-4 inline-block text-sm ${authLink}`}>
        {t("auth.joinBusiness.loginLink")}
      </Link>
    </div>
  );
}

function SuccessIcon({ icon }: { icon: ReactNode }) {
  return (
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200 text-emerald-600 dark:bg-emerald-500/15 dark:ring-emerald-500/30 dark:text-emerald-300">
      {icon}
    </div>
  );
}
